import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  BookCopy,
  LayoutList,
} from "lucide-react";
import { toast } from "sonner";

const PROGRAM_ACCENT = {
  CONV: "bg-[#1B2C5A] text-white",
  CRECHE: "bg-pink-200 text-pink-900",
  ISL: "bg-emerald-200 text-emerald-900",
  TAHFEEZ: "bg-amber-200 text-amber-900",
};

const Programs = () => {
  const { user } = useAuth();
  const canManage =
    ["super_admin", "director"].includes(user?.role) ||
    (user?.roles || []).some((r) => ["super_admin", "director"].includes(r));

  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [currentTerms, setCurrentTerms] = useState([]);
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [expandedClass, setExpandedClass] = useState(null);

  // dialogs
  const [progOpen, setProgOpen] = useState(false);
  const [progForm, setProgForm] = useState({
    name: "",
    code: "",
    schedule: "",
    description: "",
  });
  const [classOpen, setClassOpen] = useState(false);
  const [classForm, setClassForm] = useState({
    program_id: "",
    name: "",
    order_index: 0,
  });
  const [armOpen, setArmOpen] = useState(false);
  const [armForm, setArmForm] = useState({ class_id: "", name: "" });
  const [sessionOpen, setSessionOpen] = useState(false);
  const [sessionForm, setSessionForm] = useState({ name: "", start_date: "", end_date: "", is_current: false });

  const load = async () => {
    try {
      const [{ data }, sessionResponse] = await Promise.all([
        api.get("/programs"),
        api.get("/academic-sessions"),
      ]);
      const enriched = await Promise.all(
        data.map(async (p) => {
          const cls = (
            await api.get("/classes", { params: { program_id: p.id } })
          ).data;
          return { ...p, classes: cls };
        }),
      );
      const sessionList = sessionResponse.data || [];
      const currentSession = sessionList.find((session) => session.is_current);
      setPrograms(enriched);
      setSessions(sessionList);
      if (!currentSession) return;
      const termsResponse = await api.get(`/academic-sessions/${currentSession.id}/terms`);
      setCurrentTerms(termsResponse.data || []);
      try {
        const periodResponse = await api.get("/academic-period/current");
        setCurrentPeriod(periodResponse.data);
      } catch (e) {
        setCurrentPeriod(null);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const createProgram = async () => {
    if (!progForm.name || !progForm.code)
      return toast.error("Name and code required");
    try {
      await api.post("/programs", progForm);
      toast.success("Program created");
      setProgOpen(false);
      setProgForm({ name: "", code: "", schedule: "", description: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteProgram = async (id) => {
    if (!window.confirm("Delete this program and all its classes/arms?"))
      return;
    try {
      await api.delete(`/programs/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openAddClass = (program_id) => {
    setClassForm({ program_id, name: "", order_index: 0 });
    setClassOpen(true);
  };
  const createClass = async () => {
    if (!classForm.name) return toast.error("Class name required");
    try {
      await api.post("/classes", classForm);
      toast.success("Class added");
      setClassOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteClass = async (id) => {
    if (!window.confirm("Delete this class and its arms?")) return;
    try {
      await api.delete(`/classes/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openAddArm = (class_id) => {
    setArmForm({ class_id, name: "" });
    setArmOpen(true);
  };
  const createArm = async () => {
    if (!armForm.name) return toast.error("Arm name required");
    try {
      await api.post("/arms", armForm);
      toast.success("Arm added");
      setArmOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteArm = async (id) => {
    if (!window.confirm("Delete this arm?")) return;
    try {
      await api.delete(`/arms/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createSession = async () => {
    if (!sessionForm.name.trim()) return toast.error("Academic session name is required");
    try {
      await api.post("/academic-sessions", { ...sessionForm, name: sessionForm.name.trim() });
      toast.success("Academic session created");
      setSessionOpen(false);
      setSessionForm({ name: "", start_date: "", end_date: "", is_current: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setCurrentSession = async (id) => {
    try {
      const ensured = await api.post(`/academic-sessions/${id}/terms/ensure`);
      const firstTerm = ensured.data.find((term) => term.code === "first");
      await api.patch(`/academic-sessions/${id}/set-current`, { term_id: firstTerm?.id });
      toast.success("Current academic session updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const ensureCurrentSessionTerms = async () => {
    const currentSession = sessions.find((session) => session.is_current);
    if (!currentSession) return;
    try {
      await api.post(`/academic-sessions/${currentSession.id}/terms/ensure`);
      toast.success("Standard terms are ready");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setCurrentTerm = async (termId) => {
    try {
      await api.patch(`/academic-terms/${termId}/set-current`);
      toast.success("Current academic term updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setTermOpen = async (term, isOpen) => {
    try {
      await api.patch(`/academic-terms/${term.id}/open`, { is_open: isOpen });
      toast.success(isOpen ? "Academic term opened" : "Academic term closed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="programs-page">
      <PageHeader
        overline="Academic Setup"
        title="Programs, Classes & Arms"
        description="DFA operates parallel programs (Conventional, Creche, Islamiyyah, Tahfeez). Manage their classes and arms here. Students enroll in one or more programs from their profile."
        actions={
          canManage && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSessionOpen(true)} data-testid="add-session-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New Session
              </Button>
              <Button
                onClick={() => setProgOpen(true)}
                className="bg-[#1B2C5A] hover:bg-[#14224A] text-white"
                data-testid="add-program-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" /> New Program
              </Button>
            </div>
          )
        }
      />

      <Card className="mb-4 p-5 border-[#E2E8F0]" data-testid="academic-sessions-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg text-[#1B2C5A]">Academic Sessions</h2>
            <p className="text-sm text-[#64748B]">Create the upcoming session here before using it as a Promotion destination.</p>
          </div>
          {canManage && <Button size="sm" variant="outline" onClick={() => setSessionOpen(true)}><Plus className="w-4 h-4 mr-1" /> New Session</Button>}
        </div>
        {sessions.length === 0 ? (
          <p className="mt-4 text-sm text-[#64748B]">No academic sessions have been created.</p>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            {sessions.map((session) => (
              <div key={session.id} className="flex items-center gap-2 rounded-lg border border-[#E2E8F0] bg-[#FDFBF7] px-3 py-2 text-sm">
                <span className="font-medium text-[#1B2C5A]">{session.name}</span>
                {session.is_current ? (
                  <span className="rounded-full bg-[#9DCD3F]/20 px-2 py-0.5 text-xs text-[#1B2C5A]">Current</span>
                ) : canManage ? (
                  <Button size="sm" variant="ghost" onClick={() => setCurrentSession(session.id)}>Set current</Button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="mb-4 p-5 border-[#E2E8F0]" data-testid="academic-period-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg text-[#1B2C5A]">Current Academic Period</h2>
            <p className="text-sm text-[#64748B]">
              {currentPeriod
                ? `${currentPeriod.session.name} · ${currentPeriod.term.name}`
                : "A current term has not been configured for the current session."}
            </p>
          </div>
          {canManage && currentTerms.length === 0 && (
            <Button size="sm" variant="outline" onClick={ensureCurrentSessionTerms}>Set up standard terms</Button>
          )}
        </div>
        {currentTerms.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {currentTerms.map((term) => (
              <div key={term.id} className="flex items-center gap-2 rounded-lg border border-[#E2E8F0] bg-[#FDFBF7] px-3 py-2 text-sm">
                <span className="font-medium text-[#1B2C5A]">{term.name}</span>
                {term.is_current && <span className="rounded-full bg-[#9DCD3F]/20 px-2 py-0.5 text-xs text-[#1B2C5A]">Current</span>}
                <span className={`rounded-full px-2 py-0.5 text-xs ${term.is_open ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                  {term.is_open ? "Open" : "Closed"}
                </span>
                {canManage && !term.is_current && <Button size="sm" variant="ghost" onClick={() => setCurrentTerm(term.id)}>Set current</Button>}
                {canManage && <Button size="sm" variant="ghost" onClick={() => setTermOpen(term, !term.is_open)}>{term.is_open ? "Close" : "Open"}</Button>}
              </div>
            ))}
          </div>
        )}
      </Card>

      {programs.length === 0 ? (
        <EmptyState message="No programs yet." />
      ) : (
        <div className="space-y-4">
          {programs.map((p) => {
            const isOpen = expandedProgram === p.id;
            return (
              <Card
                key={p.id}
                className="border-[#E2E8F0] overflow-hidden"
                data-testid={`program-${p.id}`}
              >
                <div
                  className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-[#FDFBF7]"
                  onClick={() => setExpandedProgram(isOpen ? null : p.id)}
                >
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4 text-[#1B2C5A]" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-[#1B2C5A]" />
                  )}
                  <div
                    className={
                      "w-10 h-10 rounded-lg grid place-items-center " +
                      (PROGRAM_ACCENT[p.code] || "bg-slate-200")
                    }
                  >
                    <GraduationCap className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-display text-lg text-[#1B2C5A]">
                        {p.name}
                      </span>
                      <span className="font-mono text-xs text-[#475569]">
                        {p.code}
                      </span>
                      {!p.is_active && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                          Inactive
                        </span>
                      )}
                    </div>
                    {p.schedule && (
                      <div className="text-xs text-[#475569] mt-0.5">
                        {p.schedule}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-[#475569] flex items-center gap-3">
                    <span className="inline-flex items-center gap-1">
                      <BookCopy className="w-3.5 h-3.5" /> {p.classes.length}{" "}
                      classes
                    </span>
                  </div>
                  {canManage && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteProgram(p.id);
                      }}
                      className="text-red-700"
                      data-testid={`delete-program-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {isOpen && (
                  <div className="px-5 pb-5 border-t border-[#E2E8F0] bg-[#FDFBF7]">
                    <div className="flex items-center justify-between py-3">
                      <h4 className="text-sm font-medium text-[#1B2C5A]">
                        Classes
                      </h4>
                      {canManage && (
                        <Button
                          size="sm"
                          onClick={() => openAddClass(p.id)}
                          className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A]"
                          data-testid={`add-class-${p.id}`}
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add Class
                        </Button>
                      )}
                    </div>
                    {p.classes.length === 0 ? (
                      <p className="text-xs text-[#475569] py-2">
                        No classes yet. Add the first class to start enrolling
                        students.
                      </p>
                    ) : (
                      <ul className="space-y-1.5">
                        {p.classes.map((c) => {
                          const classOpenLocal = expandedClass === c.id;
                          return (
                            <li
                              key={c.id}
                              className="rounded-lg bg-white border border-[#E2E8F0]"
                              data-testid={`class-${c.id}`}
                            >
                              <div
                                className="px-3 py-2 flex items-center gap-3 cursor-pointer hover:bg-[#FDFBF7]"
                                onClick={() =>
                                  setExpandedClass(classOpenLocal ? null : c.id)
                                }
                              >
                                {classOpenLocal ? (
                                  <ChevronDown className="w-3.5 h-3.5 text-[#1B2C5A]" />
                                ) : (
                                  <ChevronRight className="w-3.5 h-3.5 text-[#1B2C5A]" />
                                )}
                                <LayoutList className="w-4 h-4 text-[#9DCD3F]" />
                                <span className="flex-1 text-sm text-[#1B2C5A] font-medium">
                                  {c.name}
                                </span>
                                <span className="text-xs text-[#475569]">
                                  {c.arms?.length || 0} arms
                                </span>
                                {canManage && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteClass(c.id);
                                    }}
                                    className="text-red-700"
                                    data-testid={`delete-class-${c.id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </div>
                              {classOpenLocal && (
                                <div className="px-3 pb-3 border-t border-[#E2E8F0]">
                                  <div className="flex items-center justify-between py-2">
                                    <span className="text-xs text-[#475569]">
                                      Arms within {c.name}
                                    </span>
                                    {canManage && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => openAddArm(c.id)}
                                        data-testid={`add-arm-${c.id}`}
                                      >
                                        <Plus className="w-3 h-3 mr-1" /> Arm
                                      </Button>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-1.5">
                                    {(c.arms || []).map((a) => (
                                      <span
                                        key={a.id}
                                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-[#9DCD3F]/15 text-[#1B2C5A] border border-[#9DCD3F]/30"
                                        data-testid={`arm-${a.id}`}
                                      >
                                        {a.name}
                                        {canManage && (
                                          <button
                                            onClick={() => deleteArm(a.id)}
                                            className="text-red-700 hover:text-red-900"
                                            aria-label="Delete arm"
                                            data-testid={`delete-arm-${a.id}`}
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        )}
                                      </span>
                                    ))}
                                    {(c.arms || []).length === 0 && (
                                      <span className="text-xs text-[#475569]">
                                        No arms yet.
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={sessionOpen} onOpenChange={setSessionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A]">New Academic Session</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Fld label="Session name">
                <Input value={sessionForm.name} onChange={(e) => setSessionForm({ ...sessionForm, name: e.target.value })} placeholder="e.g. 2026/2027" data-testid="session-name" />
              </Fld>
            </div>
            <Fld label="Start date (optional)">
              <Input type="date" value={sessionForm.start_date} onChange={(e) => setSessionForm({ ...sessionForm, start_date: e.target.value })} />
            </Fld>
            <Fld label="End date (optional)">
              <Input type="date" value={sessionForm.end_date} onChange={(e) => setSessionForm({ ...sessionForm, end_date: e.target.value })} />
            </Fld>
          </div>
          <label className="mt-1 flex items-center gap-2 text-sm text-[#1B2C5A]">
            <input type="checkbox" checked={sessionForm.is_current} onChange={(e) => setSessionForm({ ...sessionForm, is_current: e.target.checked })} />
            Mark as the current academic session
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSessionOpen(false)}>Cancel</Button>
            <Button onClick={createSession} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="session-submit">Create session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Program Dialog */}
      <Dialog open={progOpen} onOpenChange={setProgOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A]">
              New Program
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Name">
              <Input
                value={progForm.name}
                onChange={(e) =>
                  setProgForm({ ...progForm, name: e.target.value })
                }
                data-testid="program-name"
              />
            </Fld>
            <Fld label="Code">
              <Input
                value={progForm.code}
                onChange={(e) =>
                  setProgForm({
                    ...progForm,
                    code: e.target.value.toUpperCase(),
                  })
                }
                placeholder="CONV / ISL / etc."
                data-testid="program-code"
              />
            </Fld>
            <div className="col-span-2">
              <Fld label="Schedule">
                <Input
                  value={progForm.schedule}
                  onChange={(e) =>
                    setProgForm({ ...progForm, schedule: e.target.value })
                  }
                  placeholder="e.g. Mon–Fri Morning"
                />
              </Fld>
            </div>
            <div className="col-span-2">
              <Fld label="Description">
                <Input
                  value={progForm.description}
                  onChange={(e) =>
                    setProgForm({ ...progForm, description: e.target.value })
                  }
                />
              </Fld>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProgOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={createProgram}
              className="bg-[#1B2C5A] hover:bg-[#14224A] text-white"
              data-testid="program-submit"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Class Dialog */}
      <Dialog open={classOpen} onOpenChange={setClassOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A]">
              New Class
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Fld label="Class Name">
              <Input
                value={classForm.name}
                onChange={(e) =>
                  setClassForm({ ...classForm, name: e.target.value })
                }
                placeholder="e.g. Basic 4 / Islamiyyah 3 / Intermediate"
                data-testid="class-name"
              />
            </Fld>
            <Fld label="Order">
              <Input
                type="number"
                value={classForm.order_index}
                onChange={(e) =>
                  setClassForm({
                    ...classForm,
                    order_index: Number(e.target.value),
                  })
                }
              />
            </Fld>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClassOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={createClass}
              className="bg-[#1B2C5A] hover:bg-[#14224A] text-white"
              data-testid="class-submit"
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Arm Dialog */}
      <Dialog open={armOpen} onOpenChange={setArmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A]">
              New Arm
            </DialogTitle>
          </DialogHeader>
          <Fld label="Arm Name">
            <Input
              value={armForm.name}
              onChange={(e) => setArmForm({ ...armForm, name: e.target.value })}
              placeholder="A / B / Group A"
              data-testid="arm-name"
            />
          </Fld>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArmOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={createArm}
              className="bg-[#1B2C5A] hover:bg-[#14224A] text-white"
              data-testid="arm-submit"
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Fld = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[#1B2C5A] text-sm">{label}</Label>
    {children}
  </div>
);

export default Programs;
